import React, { Component } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Legend,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import styled from 'styled-components';
import Diamond from './Shapes/Diamond';
import DiamondActive from './Shapes/DiamondActive';
import Rectangle from './Shapes/Rectangle';
import RectangleActive from './Shapes/RectangleActive';
import Triangle from './Shapes/Triangle';
import TriangleActive from './Shapes/TriangleActive';
import Circle from './Shapes/Circle';
import CircleActive from './Shapes/CircleActive';
import SmallCircle from './Shapes/SmallCircle';
import SmallCircleActive from './Shapes/SmallCircleActive';

import LineLegend from './LineLegend';
import get from 'lodash/get';

const LineGraphContainer = styled.div`
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  .recharts-cartesian-axis-line {
    stroke: #e4e4e4;
  }

  .yAxis .recharts-cartesian-axis-tick:first-child {
    display: none !important;
  }
  .recharts-legend-wrapper {
    bottom: 0 !important;
  }
  .recharts-legend-item-text {
    font-family: ${(props) => props.theme.fonts.oswald};
    font-size: 12px;
    color: #18262c;
  }
  .legendText {
    display: inline;
    margin-left: 6px;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-size: 12px;
    color: #18262c;
  }
`;

const TooltipContainer = styled.div`
  padding: 10px;
  min-width: 150px;
`;

const LineInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  :not(:last-child) {
    margin-bottom: 6px;
  }
  .tooltipLineLabel {
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-size: 12px;
    color: #4a4a4a;
  }
  .tooltipLineValue {
    margin-left: auto;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-weight: 600;
    font-size: 12px;
    color: ${(props) => props.color};
  }
  .evolutionContainer {
    margin-left: 5px;
    display: flex;
    flex-direction: row;
    align-items: center;
  }
  .evolutionValue {
    margin-left: 5px;
  }
  .evolutionNeutral {
    color: #a5aaaf;
  }
  .evolutionPositive {
    color: #6cc89f;
  }
  .evolutionNegative {
    color: #e25d34;
  }
`;

const TickContainer = styled.text`
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  fill: #2b3d54;
  font-size: 12px;
`;

export default class LineGraph extends Component {
  constructor(props) {
    super(props);
    this.state = {
      points: [],
      position: { x: 0, y: 0 }
    };
  }

  renderTickAxis({ x, y, payload, paddingRight, paddingTop }) {
    return (
      <TickContainer x={x - paddingRight} y={y + paddingTop}>
        {payload.value}
      </TickContainer>
    );
  }

  formatData() {
    const { data, labels, sortMethod } = this.props;
    const aggregatedData = [];
    data.forEach((set, indexSet) => {
      set.forEach((item, index) => {
        let aggregatedItem = {};
        aggregatedItem.name = item.name;
        aggregatedItem[labels[indexSet]] =
          item.value && item.value.value ? item.value.value : item.value;
        aggregatedItem[labels[indexSet] + 'Raw'] = item.raw;
        if (sortMethod) {
          let indexExist = aggregatedData.findIndex(
            (existItem) => existItem.name === aggregatedItem.name
          );
          if (indexExist >= 0) {
            aggregatedData[indexExist] = {
              ...aggregatedData[indexExist],
              ...aggregatedItem
            };
          } else {
            aggregatedData.push(aggregatedItem);
          }
        } else {
          if (aggregatedData[index]) {
            aggregatedData[index] = {
              ...aggregatedData[index],
              ...aggregatedItem
            };
          } else {
            aggregatedData.push(aggregatedItem);
          }
        }
      });
    });
    if (sortMethod) {
      aggregatedData.sort((a, b) => sortMethod(a, b));
    }
    return aggregatedData;
  }

  getValueTooltip(label, index, data) {
    if (data[index][label + 'Raw']) {
      return data[index][label + 'Raw'];
    }
    if (data[index][label]) {
      return data[index][label];
    }
    return null;
  }

  renderLineTooltip(data, labels, colors, activeTooltipIndex, formattedData) {
    const { acceptZeros } = this.props;
    let renderedLines = [];

    data.forEach((set, index) => {
      const valueTooltip = this.getValueTooltip(
        labels[index],
        activeTooltipIndex,
        formattedData
      );

      if (set && (valueTooltip || acceptZeros) && labels[index]) {
        const value = acceptZeros ? Number(valueTooltip) : valueTooltip;
        const currency = get(set[index], 'currency', null);

        renderedLines.push(
          <LineInfo
            key={`lineInfo-${activeTooltipIndex}`}
            color={colors[index]}
          >
            <p className="tooltipLineLabel">{labels[index]}</p>
            <p className="tooltipLineValue">
              {currency
                ? value.toLocaleString('fr-FR', {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 1
                  })
                : value}
            </p>
          </LineInfo>
        );
      }
    });

    return renderedLines;
  }

  getPosition(coordinates, direction) {
    const { tooltipOffset, tooltipOffsetRight, tooltipOffsetLeft } = this.props;
    if (tooltipOffsetRight && direction === 'right') {
      return {
        x: coordinates.x + tooltipOffsetRight.x,
        y: coordinates.y + tooltipOffsetRight.y
      };
    }
    if (tooltipOffsetLeft && direction === 'left') {
      return {
        x: coordinates.x + tooltipOffsetLeft.x,
        y: coordinates.y + tooltipOffsetLeft.y
      };
    }
    if (tooltipOffset && direction === 'top') {
      return {
        x: coordinates.x + tooltipOffset.x,
        y: coordinates.y + tooltipOffset.y
      };
    }
    return coordinates;
  }

  renderTooltip(formattedData) {
    const { labels, tooltip, data, tooltipContent } = this.props;
    const { position, activeTooltipIndex } = this.state;
    let direction = 'top';
    const colors = [
      '#e25d34',
      'rgba(43, 61, 84,1)',
      'rgba(43, 61, 84,0.85)',
      'rgba(43, 61, 84,0.7)',
      'rgba(43, 61, 84,0.5)',
      'rgba(43, 61, 84,0.35)',
      'rgba(43, 61, 84,0.2)'
    ];
    if (tooltip === false) {
      return null;
    }
    let coordinates = { x: position.x - 87, y: position.y - 120 };
    let limit = 1;
    if (formattedData.length > 9) {
      limit = parseInt(formattedData.length / 5);
    }
    if (
      activeTooltipIndex >= formattedData.length - limit &&
      formattedData.length !== 1
    ) {
      coordinates.x = coordinates.x - 250;
      coordinates.y = coordinates.y + 100;
    }
    if (
      activeTooltipIndex <= limit &&
      formattedData.length > 5 &&
      formattedData.length !== 1
    ) {
      coordinates.x = coordinates.x + 250;
      coordinates.y = coordinates.y + 50;
    }
    if (
      activeTooltipIndex >= formattedData.length - limit &&
      formattedData.length !== 1
    ) {
      direction = 'left';
    } else if (
      activeTooltipIndex <= limit &&
      formattedData.length > 5 &&
      formattedData.length !== 1
    ) {
      direction = 'right';
    } else {
      direction = 'top';
    }

    const finalPosition = this.getPosition(coordinates, direction);

    return (
      <Tooltip
        content={
          <div
            className={`rc-tooltip overlayTooltip rc-tooltip-placement-${direction} rc-tooltip-inner`}
          >
            <div className="rc-tooltip-content">
              <div
                className="rc-tooltip-arrow"
                style={this.helperColorArrow()}
              />
              <div className="rc-tooltip-inner" role="tooltip">
                <TooltipContainer>
                  {tooltipContent
                    ? tooltipContent(data, activeTooltipIndex)
                    : this.renderLineTooltip(
                        data,
                        labels,
                        colors,
                        activeTooltipIndex,
                        formattedData
                      )}
                </TooltipContainer>
              </div>
            </div>
          </div>
        }
        cursor={false}
        position={finalPosition}
        {...tooltip}
      />
    );
  }

  helperColorArrow() {
    const { colorArrow } = this.props;
    const { direction } = this.state;
    if (colorArrow) {
      const style = {};
      Object.keys(colorArrow).map((key) => {
        style[`border${key}Color`] =
          direction === key.toLowerCase() ? colorArrow[key] : 'transparent';
      });
      return style;
    }
  }

  getShape(active, index) {
    const { onlyCircle, smallShape } = this.props;

    if (smallShape) {
      return active ? SmallCircleActive : SmallCircle;
    }

    if (onlyCircle) {
      return active ? CircleActive : Circle;
    }

    const shapes = [Circle, Triangle, Diamond, Rectangle];
    const activeShapes = [
      CircleActive,
      TriangleActive,
      DiamondActive,
      RectangleActive
    ];
    if (active) {
      return activeShapes[index];
    }
    return shapes[index];
  }

  renderShape(libraryProps, color, index) {
    const shapeIndex = parseInt(index % 4, 10);
    const { activeTooltipIndex } = this.state;

    const Shape = this.getShape(
      activeTooltipIndex === libraryProps.index,
      shapeIndex
    );

    return (
      <Shape
        {...libraryProps}
        color={color}
        active={activeTooltipIndex === libraryProps.index}
        mouseLeave={() =>
          this.setState({ hovering: false, activeTooltipIndex: null })
        }
        mouseEnter={(x, y, index) =>
          this.setState({
            hovering: true,
            position: { x: x, y: y },
            activeTooltipIndex: index
          })
        }
      />
    );
  }

  getReferenceLine(data, index) {
    if (data && data[index]) {
      return data[index].name;
    }
    return undefined;
  }

  render() {
    const {
      data,
      labels,
      height,
      yAxisProps,
      xAxisProps,
      strokeDasharray,
      referenceLines,
      gridProps,
      type,
      onlyCircle,
      customColors,
      marginsChart
    } = this.props;
    const colors = customColors || [
      '#e25d34',
      'rgba(43, 61, 84,1)',
      'rgba(43, 61, 84,0.85)',
      'rgba(43, 61, 84,0.7)',
      'rgba(43, 61, 84,0.5)',
      'rgba(43, 61, 84,0.35)',
      'rgba(43, 61, 84,0.2)'
    ];
    const formattedData = this.formatData();
    const { hovering, position, activeTooltipIndex } = this.state;
    const referenceLineValue = this.getReferenceLine(
      formattedData,
      activeTooltipIndex
    );

    return (
      <LineGraphContainer>
        <ResponsiveContainer height={height ? height : 550} width="100%">
          <LineChart
            height={height ? height : 550}
            data={formattedData}
            margin={marginsChart ? marginsChart : { right: 20 }}
            onMouseMove={(props) =>
              props &&
              (props.chartX < position.x - 20 ||
                props.chartX > position.x + 20 ||
                props.chartY < position.y - 20 ||
                props.chartY > position.y + 20)
                ? this.setState({ hovering: false, activeTooltipIndex: null })
                : null
            }
          >
            <CartesianGrid vertical={false} stroke="#e4e4e4" {...gridProps} />
            <XAxis
              interval="preserveEnd"
              type="category"
              tickLine={false}
              dataKey="name"
              tick={(props) =>
                this.renderTickAxis({
                  ...props,
                  paddingTop: 3,
                  paddingRight: 30
                })
              }
              tickMargin={15}
              {...xAxisProps}
            />
            <YAxis
              type="number"
              width={80}
              tickSize={0}
              tickFormatter={this.props.yTickFormatter}
              domain={[
                (dataMin) => dataMin - dataMin / 4,
                (dataMax) => dataMax + dataMax / 4
              ]}
              tickMargin={15}
              tickLine={{ stroke: '#e4e4e4' }}
              {...yAxisProps}
            />
            {hovering && this.renderTooltip(formattedData)}
            <Legend
              content={<LineLegend onlyCircle={onlyCircle} />}
              colors={colors}
              labels={labels}
              type="line"
            />
            {data.map((set, index) => (
              <Line
                points={[{ x: 10, y: 10 }]}
                isAnimationActive={false}
                connectNulls
                key={index}
                dot={(libraryProps) =>
                  libraryProps.value
                    ? this.renderShape(libraryProps, colors[index], index)
                    : null
                }
                activeDot={(libraryProps) =>
                  this.renderShape(libraryProps, colors[index], index)
                }
                dataKey={labels[index]}
                strokeWidth={2}
                type={type && type[index] ? type[index] : 'linear'}
                strokeDasharray={
                  strokeDasharray && strokeDasharray[index]
                    ? strokeDasharray[index]
                    : null
                }
                stroke={colors[index]}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </LineGraphContainer>
    );
  }
}
